import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AlertProvider } from './context/AlertContext';
import Header from './components/layout/Header';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import FleetMap from './pages/Fleetmap';
import Vehicles from './pages/Vehicles';
import Drivers from './pages/Drivers';
import AlertsPage from './pages/Alerts';
import Companies from './pages/Companies';
import TalentHub from './pages/TelentHub';
import CompanyProfile from './pages/CompanyProfile';
// IMPORT DRIVER DASHBOARD
import DriverDashboard from './components/Drivers/DriverDashboard'; // ADD THIS LINE

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [userRole, setUserRole] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (user && token) {
      try {
        const userData = JSON.parse(user);
        setIsAuthenticated(true);
        setUserRole(userData.role);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e28743 0%, #304a3f 100%)' }}>
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check if user has required role
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Main Layout for authenticated pages - Admin Version
const MainLayout = ({ children }) => {
  const [activePage, setActivePage] = React.useState('Overview');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="min-h-screen bg-gray-100">
      <Header 
        activePage={activePage}
        setActivePage={setActivePage}
      />
      
      <main className="pt-16">
        <div className="px-4 py-6">
          {children}
        </div>
      </main>
    </div>
  );
};

// Driver Layout - Simplified without admin header
const DriverLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-100">
      <main>
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AlertProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              {/* Protected Admin routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Fleet Map route */}
              <Route 
                path="/fleet-map" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                    <MainLayout>
                      <FleetMap />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Company Admin & Super Admin routes */}
              <Route 
                path="/vehicles" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                    <MainLayout>
                      <Vehicles />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/drivers" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                    <MainLayout>
                      <Drivers />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Alerts Page - Accessible to all authenticated users */}
              <Route 
                path="/alerts" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AlertsPage />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Super Admin only routes */}
              <Route 
                path="/companies" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <MainLayout>
                      <Companies />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/company-profile" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                    <MainLayout>
                      <CompanyProfile />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/talent-hub" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                    <MainLayout>
                      <TalentHub />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />

              {/* DRIVER DASHBOARD - COMPLETE PAGE */}
              <Route 
                path="/driver-dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['driver']}>
                    <DriverLayout>
                      <DriverDashboard />
                    </DriverLayout>
                  </ProtectedRoute>
                } 
              />

              {/* Driver-specific routes */}
              <Route 
                path="/driver-alerts" 
                element={
                  <ProtectedRoute allowedRoles={['driver']}>
                    <DriverLayout>
                      <div className="p-6 bg-white rounded-lg shadow">
                        <h1 className="text-2xl font-bold text-gray-800">Driver Alerts</h1>
                        <p className="text-gray-600 mt-2">Driver-specific alerts page</p>
                      </div>
                    </DriverLayout>
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/driver-profile" 
                element={
                  <ProtectedRoute allowedRoles={['driver']}>
                    <DriverLayout>
                      <div className="p-6 bg-white rounded-lg shadow">
                        <h1 className="text-2xl font-bold text-gray-800">Driver Profile</h1>
                        <p className="text-gray-600 mt-2">Driver profile management</p>
                      </div>
                    </DriverLayout>
                  </ProtectedRoute>
                } 
              />

              {/* Redirect based on user role */}
              <Route path="*" element={<NavigateToRole />} />
            </Routes>
          </div>
        </Router>
      </AlertProvider>
    </AuthProvider>
  );
}

// Component to redirect based on user role
const NavigateToRole = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role;
  
  if (role === 'driver') {
    return <Navigate to="/driver-dashboard" />;
  } else if (role === 'super_admin' || role === 'company_admin') {
    return <Navigate to="/dashboard" />;
  } else {
    return <Navigate to="/login" />;
  }
};

export default App;